import React, { FC, useCallback, useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { StyledFilterWrapper } from 'molecules/Filter/Filter.styles';
import { FilterAutocomplete } from 'atoms/TableFilter';
import { StyledPaper } from 'pages/ContactsPage/Contacts.styles';
import ExpenseReportTable, {
  ExpenseReportRow,
} from 'organisms/ExpenseReportTable';
import { debounce } from 'lodash-es';
import axios, { AxiosResponse } from 'axios';

const ExpenseReport: FC<{ hsId?: string }> = ({ hsId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<{
    expense_reports: ExpenseReportRow[];
    total: number;
  }>({ expense_reports: [], total: 0 });

  const [search, setSearch] = useState<string | undefined>('');

  /** debounce handler for two types: textfield and autocomplete */
  const debouncedChangeHandler = useCallback(
    debounce((event, value) => {
      setSearch(value || event?.target?.value || '');
    }, 1000),
    []
  );

  useEffect(() => {
    fetchPageData({
      page: 1,
      limit: 20,
      sort_by: 'created_at',
      sort_order: 'desc',
    });
  }, [search]);

  const fetchPageData = async (pagination: {
    page: number;
    limit: number;
    sort_by: string;
    sort_order: string;
  }) => {
    try {
      setLoading(true);
      const response: AxiosResponse<{
        expense_reports: ExpenseReportRow[];
        total: number;
      }> = await axios.get(
        `${process.env.REACT_APP_USER_SERVICE}/expense-reports`,
        {
          params: {
            ...prepareParams(),
            page: pagination.page || 1,
            per_page: pagination.limit || 20,
            sort_by: pagination.sort_by || 'created_at',
            sort_order: pagination.sort_order || 'desc',
          },
        }
      );

      setData(response?.data || { expense_reports: [], total: 0 });
      return response?.data || { expense_reports: [], total: 0 };
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
      setData({ expense_reports: [], total: 0 });
      return { expense_reports: [], total: 0 };
    } finally {
      setLoading(false);
    }
  };

  function prepareParams() {
    return {
      ...(search?.trim() ? { search } : {}),
      // If this component used in specific company page:
      ...(hsId ? { company_hs_id: hsId } : {}),
    };
  }

  // On a specific company page the results are already scoped to that
  // company, so searching by company name makes no sense there.
  const searchHint = hsId
    ? 'Search by: Project name, Expense name'
    : 'Search by: Company name, Project name, Expense name';

  return (
    <StyledPaper>
      <StyledFilterWrapper>
        <Stack direction="row" gap={3} flexWrap="wrap" py="20px">
          <FilterAutocomplete
            isFreeSolo={!!search}
            value={search}
            options={[]}
            placeholder={searchHint}
            tooltip={searchHint}
            onChange={debouncedChangeHandler}
            onReset={() => setSearch('')}
          />
        </Stack>
      </StyledFilterWrapper>

      <ExpenseReportTable
        isCompany={Boolean(hsId)}
        loading={loading}
        data={data}
        onChange={fetchPageData}
      />
    </StyledPaper>
  );
};

export default ExpenseReport;
